import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2 } from 'lucide-react';
import { Button } from './ui/Button';
import DateTimePicker from './ui/DateTimePicker';

export default function DeployConfigurationModal({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
  loadingMode = null,  // 'deploying' | 'scheduling' | null
}) {
  const [deployOption, setDeployOption] = useState('now'); // 'now' | 'scheduled'
  const [scheduledDateTime, setScheduledDateTime] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setDeployOption('now');
      setScheduledDateTime(null);
    }
  }, [isOpen]);

  // Validate that scheduled time is in the future
  const isScheduledInPast = deployOption === 'scheduled' && scheduledDateTime && scheduledDateTime <= new Date();
  const isDeployDisabled = (deployOption === 'scheduled' && !scheduledDateTime) || isScheduledInPast;

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="relative w-full max-w-xl rounded-lg bg-background shadow-lg text-left">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-semibold text-foreground">
              Deploy Configuration
            </h2>

            <button
              onClick={onClose}
              disabled={isLoading}
              className="text-muted-foreground hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <h3 className="mt-4 mb-3 text-lg font-medium text-muted-foreground">
            Deployment Schedule
          </h3>

          <div className="space-y-3">
            <label
              className={[
                'block cursor-pointer rounded-md border p-4 transition-colors',
                deployOption === 'now'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-slate-200 bg-background hover:bg-slate-50',
              ].join(' ')}
            >
              <div className="flex items-start gap-3">
                <input
                  type="radio"
                  name="deploySchedule"
                  checked={deployOption === 'now'}
                  onChange={() => setDeployOption('now')}
                  className="mt-1.5"
                />
                <div>
                  <div className="text-base font-semibold text-foreground">
                    Deploy Now
                  </div>
                  <div className="mt-1 text-sm text-muted-foreground">
                    Configuration will be active immediately for all new file loads
                  </div>
                </div>
              </div>
            </label>

            <label
              className={[
                'block cursor-pointer rounded-md border p-4 transition-colors',
                deployOption === 'scheduled'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-slate-200 bg-background hover:bg-slate-50',
              ].join(' ')}
            >
              <div className="flex items-start gap-3">
                <input
                  type="radio"
                  name="deploySchedule"
                  checked={deployOption === 'scheduled'}
                  onChange={() => setDeployOption('scheduled')}
                  className="mt-1.5"
                />

                <div className="flex-1">
                  <div className="text-base font-semibold text-foreground">
                    Schedule Deployment
                  </div>
                  <div className="mt-1 text-sm text-muted-foreground">
                    Configuration will become active at the specified date and time (processed every minute)
                  </div>

                  {deployOption === 'scheduled' && (
                    <div className="mt-3">
                      <DateTimePicker
                        value={scheduledDateTime}
                        onChange={setScheduledDateTime}
                        minDate={new Date()}
                      />
                    </div>
                  )}
                </div>
              </div>
            </label>

            <div className="rounded-md border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-900">
              <span className="font-medium">Important:</span> Once deployed, this
              configuration will be used for all incoming file loads. The previous
              configuration will no longer be active.
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4">
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>

          <Button
            disabled={isDeployDisabled || isLoading}
            onClick={() => {
              if (!onConfirm) return;

              if (deployOption === 'now') {
                onConfirm({ mode: 'now' });
                return;
              }

              onConfirm({
                mode: 'scheduled',
                scheduledAt: scheduledDateTime.toISOString(),
              });
            }}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {loadingMode === 'scheduling' ? 'Scheduling...' : 'Deploying...'}
              </>
            ) : (
              deployOption === 'now' ? 'Deploy Configuration' : 'Schedule Deployment'
            )}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}
